import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurl } from "../../config/url";
import { FiSearch, FiFileText, FiAlertCircle } from "react-icons/fi";

const BillingHistory = () => {
  const navigate = useNavigate();
  const [billingData, setBillingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ UI-only state (no API/logic change)
  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetchBillingHistory = async () => {
      try {
        const token = localStorage.getItem("token");
        const sellerId = localStorage.getItem("sellerId");

        if (!token || !sellerId) {
          console.error("No token or seller ID available. Redirecting to login...");
          navigate("/login");
          return;
        }

        const response = await axios.get(
          `${baseurl}/api/payment/billing-history/${sellerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setBillingData(response.data.payments);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching billing history:", error);
        setError("Failed to fetch billing history");
        setLoading(false);
      }
    };

    fetchBillingHistory();
  }, [navigate]);

  const handleDetails = (payment) => {
    navigate("/invoice-detail", { state: { payment } });
  };

  // ✅ UI-only filtering (no API impact)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return billingData;

    return billingData.filter((p) => {
      const id = (p.orderCreationId || p.orderId || "").toLowerCase();
      const type = (p.paymentType || "").toLowerCase();
      const currency = (p.currency || "").toLowerCase();
      const amount = String(p.amount ?? "").toLowerCase();
      return (
        id.includes(q) ||
        type.includes(q) ||
        currency.includes(q) ||
        amount.includes(q)
      );
    });
  }, [billingData, query]);

  const totalPaid = useMemo(() => {
    return (billingData || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
  }, [billingData]);

  // ✅ Professional loading UI
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-4">
        <div className="w-full max-w-2xl bg-white border rounded-2xl p-6 shadow-sm">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-48 bg-slate-200 rounded" />
            <div className="h-4 w-80 bg-slate-200 rounded" />
            <div className="h-11 w-full bg-slate-200 rounded-xl" />
            <div className="h-56 w-full bg-slate-200 rounded-2xl" />
          </div>
          <p className="text-sm text-slate-500 mt-4">Loading billing history…</p>
        </div>
      </div>
    );
  }

  // ✅ Professional error UI
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-4">
        <div className="w-full max-w-2xl bg-white border rounded-2xl p-6 shadow-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-50 border border-red-100 px-3 py-1 text-red-700 font-bold text-sm">
            <FiAlertCircle />
            Error
          </div>
          <h2 className="text-xl font-extrabold text-slate-900 mt-3">
            Unable to load billing history
          </h2>
          <p className="text-slate-600 mt-1">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 h-11 px-5 rounded-xl bg-slate-900 text-white font-extrabold hover:bg-slate-800 transition"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ✅ Sticky professional header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Billing History
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Track all payments and open invoice details anytime.
            </p>
          </div>

          {/* ✅ Summary cards (UI only) */}
          <div className="grid grid-cols-2 gap-3 w-full sm:w-auto">
            <div className="rounded-2xl border bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-bold text-slate-500">Transactions</p>
              <p className="text-lg font-extrabold text-slate-900">
                {billingData?.length || 0}
              </p>
            </div>
            <div className="rounded-2xl border bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-bold text-slate-500">Total Amount</p>
              <p className="text-lg font-extrabold text-slate-900">
                ₹{Number(totalPaid || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* ✅ Search bar (UI only) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-5">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <FiSearch />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by Order ID, Type, Amount…"
              className="w-full h-11 pl-10 pr-4 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* ✅ Table container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-extrabold text-slate-900">Transactions</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Showing {filtered.length} of {billingData.length}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead className="bg-slate-50">
                <tr>
                  <th className="py-3 px-5 text-left text-xs font-extrabold tracking-wide text-slate-600">
                    Order ID
                  </th>
                  <th className="py-3 px-5 text-left text-xs font-extrabold tracking-wide text-slate-600">
                    Date
                  </th>
                  <th className="py-3 px-5 text-left text-xs font-extrabold tracking-wide text-slate-600">
                    Type
                  </th>
                  <th className="py-3 px-5 text-left text-xs font-extrabold tracking-wide text-slate-600">
                    Amount
                  </th>
                  <th className="py-3 px-5 text-right text-xs font-extrabold tracking-wide text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((payment, index) => {
                  const orderId = payment.orderCreationId || payment.orderId || "—";
                  const createdAt = payment.createdAt
                    ? new Date(payment.createdAt).toLocaleDateString()
                    : "—";
                  const paymentType = payment.paymentType || "—";
                  const amountText = `${payment.currency || "₹"} ${payment.amount ?? "—"}`;

                  return (
                    <tr
                      key={index}
                      className={`border-t ${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                      } hover:bg-blue-50/40 transition-colors`}
                    >
                      <td className="py-4 px-5">
                        <p className="text-sm font-extrabold text-slate-900">
                          {orderId}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Ref: {String(orderId).slice(0, 14)}…
                        </p>
                      </td>

                      <td className="py-4 px-5 text-sm text-slate-700 font-semibold">
                        {createdAt}
                      </td>

                      <td className="py-4 px-5">
                        <span className="inline-flex items-center rounded-full border bg-white px-3 py-1 text-xs font-extrabold text-slate-700">
                          {paymentType}
                        </span>
                      </td>

                      <td className="py-4 px-5">
                        <p className="text-sm font-extrabold text-slate-900">
                          {amountText}
                        </p>
                      </td>

                      <td className="py-4 px-5 text-right">
                        <button
                          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-blue-600 text-white font-extrabold
                                     hover:bg-blue-700 transition shadow-sm"
                          onClick={() => handleDetails(payment)}
                        >
                          <FiFileText />
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 px-5 text-center">
                      <p className="text-sm font-bold text-slate-700">
                        No results found
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Try searching with Order ID, payment type, or amount.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-4">
          Tip: Click <span className="font-bold">View Details</span> to open invoice details.
        </p>
      </div>
    </div>
  );
};

export default BillingHistory;